import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { RequirePermission } from "../components/auth/RequirePermission";
import { RequireRole } from "../components/auth/RequireRole";
import { RequireStationAdminArea } from "../components/auth/RequireStationAdminArea";
import { RequireInternalAffairs } from "../components/auth/RequireInternalAffairs";
import {
  KARDA_ADMIN_ROLE,
  STATION_ADMIN_ROLE,
  USERS_ADMIN_PERMISSION,
} from "../shared/auth/authorization";

const ProfilePage = lazy(() => import("../pages/Profile"));
const ManageUsersPage = lazy(() => import("../pages/Admin/ManageUsers"));
const ConnectBodycam = lazy(() => import("../pages/ConnectBodycam"));
const StationAdminLayout = lazy(
  () => import("../pages/StationAdmin/StationAdminLayout")
);
const StationAdminIndex = lazy(
  () => import("../pages/StationAdmin/StationAdminIndex")
);
const StationAdminInternalAffairs = lazy(
  () => import("../pages/StationAdmin/StationAdminInternalAffairs")
);
const KardaAdminProvider = lazy(() =>
  import("../pages/KardaAdmin/KardaAdminContext").then(
    ({ KardaAdminProvider }) => ({ default: KardaAdminProvider })
  )
);
const KardaAdminLayout = lazy(
  () => import("../pages/KardaAdmin/KardaAdminLayout")
);
const KardaAdminPage = lazy(() => import("../pages/KardaAdmin/KardaAdminPage"));
const KardaStationDetailPage = lazy(
  () => import("../pages/KardaAdmin/KardaStationDetailPage")
);
const StationDetailsSection = lazy(
  () => import("../pages/KardaAdmin/StationDetailsSection")
);
const StationUsersSection = lazy(
  () => import("../pages/KardaAdmin/StationUsersSection")
);
const StationBodycamSection = lazy(
  () => import("../pages/KardaAdmin/StationBodycamSection")
);
const UserMappingsSection = lazy(
  () => import("../pages/KardaAdmin/UserMappingsSection")
);
const StationSettingsSection = lazy(
  () => import("../pages/KardaAdmin/StationSettingsSection")
);
const TemplateUploadPage = lazy(
  () => import("../pages/TemplateUpload/TemplateUploadPage")
);
const InvestigatorAnalyticsPage = lazy(() =>
  import("../pages/InvestigatorAnalytics").then(
    ({ InvestigatorAnalyticsPage }) => ({ default: InvestigatorAnalyticsPage })
  )
);

// Mounted under ProtectedView, independently of the incident-management flag.
// These pages and all nested routes belong to Investigator's own build.
export const accountRoutes = (
  <Route
    element={
      <Suspense
        fallback={
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress aria-label="Loading page" />
          </Box>
        }
      >
        <Box sx={{ pt: 2, pb: 12 }}>
          <Outlet />
        </Box>
      </Suspense>
    }
  >
    <Route path="profile" element={<ProfilePage />} />
    <Route
      path="manage-users"
      element={
        <RequirePermission permission={USERS_ADMIN_PERMISSION}>
          <ManageUsersPage />
        </RequirePermission>
      }
    />
    <Route
      path="connect-bodycam"
      element={
        <RequirePermission permission={USERS_ADMIN_PERMISSION}>
          <ConnectBodycam />
        </RequirePermission>
      }
    />
    <Route
      path="station-admin"
      element={
        <RequireStationAdminArea>
          <RequireRole roleSlug={STATION_ADMIN_ROLE}>
            <StationAdminLayout />
          </RequireRole>
        </RequireStationAdminArea>
      }
    >
      <Route index element={<StationAdminIndex />} />
      <Route
        path="internal-affairs"
        element={
          <RequireInternalAffairs>
            <StationAdminInternalAffairs />
          </RequireInternalAffairs>
        }
      />
    </Route>
    <Route
      path="karda-admin"
      element={
        <RequireRole roleSlug={KARDA_ADMIN_ROLE}>
          <KardaAdminProvider />
        </RequireRole>
      }
    >
      <Route element={<KardaAdminLayout />}>
        <Route index element={<KardaAdminPage />} />
        {/* The ReportGen report analytics page was retired from this app;
            bookmarks of its path land on the Investigator metrics instead. */}
        <Route
          path="analytics"
          element={<Navigate to="/karda-admin/investigator-metrics" replace />}
        />
        <Route
          path="investigator-metrics"
          element={<InvestigatorAnalyticsPage />}
        />
        <Route path="templates" element={<TemplateUploadPage />} />
        <Route path="stations/:stationId" element={<KardaStationDetailPage />}>
          <Route index element={<StationDetailsSection />} />
          <Route path="users" element={<StationUsersSection />} />
          <Route path="bodycam" element={<StationBodycamSection />} />
          <Route path="mappings" element={<UserMappingsSection />} />
          <Route path="settings" element={<StationSettingsSection />} />
        </Route>
      </Route>
    </Route>
  </Route>
);
